import { gql, type TypedDocumentNode } from '@apollo/client';

import { Centered } from '@sorare/core/src/atoms/layout/Centered/index.tsx';
import { useEnableSorare27Nav } from '@sorare/core/src/hooks/useEnableSorare27Nav';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { BoardPicker } from '#src/components/pickers/BoardPicker/index.tsx';
import {
  type Mode,
  ModePicker,
} from '#src/components/pickers/ModePicker/index.tsx';
import { useLastVisitedBoardView } from '#src/hooks/useLastVisitedBoardView.ts';

import type { ProBoardPickers_careerBoard } from './__generated__/index.graphql.ts';

type Props = {
  boards: readonly ProBoardPickers_careerBoard[];
  selectedBoardId?: string;
  onSelect: (args: { boardId: string; taskId: string }) => void;
  onModeSelect: (mode: Mode) => void;
  showHighlightedBar?: boolean;
};

export const ProBoardPickers = ({
  boards,
  selectedBoardId,
  onSelect,
  onModeSelect,
  showHighlightedBar,
}: Props) => {
  const { updateBoardView } = useLastVisitedBoardView();
  const showModePicker = useEnableSorare27Nav();

  const careerBoards = boards.filter(
    isTypeFn<(typeof boards)[number], 'CareerBoard'>('CareerBoard')
  );

  return (
    <Centered
      style={{
        alignItems: 'flex-start',
        gap: 'var(--half-unit)',
      }}
      right={
        !!showModePicker && (
          <BoardPicker
            currentBoardId={selectedBoardId}
            boards={careerBoards}
            size="sm"
            highlighted={false}
            orientation="down"
            onSelect={onSelect}
          />
        )
      }
    >
      {showModePicker ? (
        <ModePicker
          showHighlightedBar={showHighlightedBar}
          onSelect={mode => {
            updateBoardView({ lastVisitedMode: mode });
            onModeSelect(mode);
          }}
        />
      ) : (
        <BoardPicker
          currentBoardId={selectedBoardId}
          boards={careerBoards}
          size="md"
          highlighted
          orientation="up"
          onSelect={onSelect}
        />
      )}
    </Centered>
  );
};

ProBoardPickers.fragments = {
  careerBoard: gql`
    fragment ProBoardPickers_careerBoard on CareerBoard {
      id
      ...BoardPicker_careerBoard
    }
    ${BoardPicker.fragments.careerBoard}
  ` as TypedDocumentNode<ProBoardPickers_careerBoard>,
};
